import { enforceFixedPageTitles } from './fixedPageTitles';

export const defaultPageContent = enforceFixedPageTitles({
  home: {
    welcomeTitle: 'Welcome to My Portfolio',
    featuredVideoTitle: 'Featured Performance',
    featuredVideoSubtitle:
      'Watch highlights from recent performances and teaching sessions.',
    upcomingEventsTitle: 'Upcoming Events',
    ctaTitle: 'Ready to Get Started?',
    ctaDescription:
      "Whether you're looking to book a performance for your special event or interested in taking music lessons, I'm here to help you achieve your musical goals.",
    features: {
      performances: {
        title: 'Live Performances',
        description:
          'Available for solo performances, duos, trios, and quartets for various events including weddings, corporate events, and private parties.',
        buttonText: 'Book a Performance'
      },
      lessons: {
        title: 'Private Lessons',
        description:
          'Personalized music lessons for students of all ages and skill levels. Learn at your own pace with customized curriculum tailored to your goals.',
        buttonText: 'Explore Music Lessons'
      },
      about: {
        title: 'About Me',
        description:
          'Learn about my musical journey, background, and what drives my passion for performance and teaching.',
        buttonText: 'About Me'
      }
    }
  },
  bookings: {
    pageTitle: 'Bookings',
    subtitle: 'Schedule a performance for your special event',
    availableForTitle: 'Available for',
    availableForItems: [
      'Private events and celebrations',
      'Corporate functions',
      'Weddings and ceremonies',
      'Restaurants, bars, and hotels',
      'Concert series and festivals',
      'Educational workshops and master classes'
    ],
    formSectionTitle: 'Book a Performance',
    formSectionSubtitle: 'Fill out the form below to request a booking for your special event'
  },
  lessons: {
    pageTitle: 'Music Lessons',
    subtitle: 'Personalized instruction for students of all ages and skill levels',
    optionsTitle: 'Available Lesson Options',
    options: [
      {
        badge: 'Most Popular',
        title: 'Private Lessons',
        description:
          'Personalized one-on-one instruction tailored to your specific goals and learning pace.',
        details: [
          '30, 45, or 60 minutes',
          'Individual attention',
          'Customized curriculum',
          'In-studio or online'
        ],
        price: 'Starting at $60 per lesson'
      },
      {
        badge: 'Best Value',
        title: 'Group Classes',
        description:
          'Learn with peers in a collaborative environment that fosters musical community.',
        details: [
          '60 minute sessions',
          '3-5 students per group',
          'Ensemble & theory focus',
          'In-studio only'
        ],
        price: 'Starting at $40 per class'
      },
      {
        badge: 'Advanced',
        title: 'Master Classes',
        description:
          'Intensive sessions for advanced students looking to refine technique and performance.',
        details: [
          '2-3 hour workshops',
          'Small group setting',
          'Advanced techniques',
          'In-studio or online'
        ],
        price: 'Starting at $100 per session'
      }
    ],
    formSectionTitle: 'Request Lessons',
    formSectionSubtitle: "Fill out the form below and I'll get back to you to arrange your lessons"
  },
  about: {
    pageTitle: 'About Me',
    pageSubtitle: ''
  },
  contact: {
    pageTitle: 'Contact Me',
    pageSubtitle: 'Get in touch for bookings, lessons, or general inquiries'
  },
  gallery: {
    pageTitle: 'Gallery',
    pageSubtitle: 'Explore my performances and creative work'
  },
  documents: {
    pageTitle: 'Documents',
    pageSubtitle: 'Sheet music, lesson materials, and resources'
  }
});

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const mergePageContent = (stored, base = defaultPageContent) => {
  const merge = (defaults, source) => {
    if (!isPlainObject(defaults)) {
      return source !== undefined ? source : defaults;
    }

    const result = { ...defaults };
    if (!isPlainObject(source)) {
      return result;
    }

    Object.keys(source).forEach((key) => {
      const defaultValue = defaults[key];
      const sourceValue = source[key];

      if (Array.isArray(defaultValue)) {
        if (!Array.isArray(sourceValue) || sourceValue.length === 0) {
          result[key] = defaultValue;
        } else if (defaultValue.length > 0 && isPlainObject(defaultValue[0])) {
          const maxLen = Math.max(defaultValue.length, sourceValue.length);
          result[key] = Array.from({ length: maxLen }, (_, index) =>
            merge(defaultValue[index] || defaultValue[0], sourceValue[index] || {})
          );
        } else {
          result[key] = sourceValue;
        }
      } else if (isPlainObject(defaultValue)) {
        result[key] = merge(defaultValue, sourceValue);
      } else if (sourceValue !== undefined && sourceValue !== null && sourceValue !== '') {
        result[key] = sourceValue;
      }
    });

    return result;
  };

  const merged = enforceFixedPageTitles(merge(base, stored || {}));

  if (merged?.home?.features?.about?.buttonText === 'Learn More') {
    merged.home.features.about.buttonText = 'About Me';
  }

  if (merged?.home?.features?.performances?.buttonText === 'Book Now') {
    merged.home.features.performances.buttonText = 'Book a Performance';
  }

  return merged;
};

const trimStringsDeep = (value) => {
  if (Array.isArray(value)) {
    return value.map(trimStringsDeep);
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => [key, trimStringsDeep(nested)])
    );
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  return value;
};

export const sanitizePageContent = (content) => {
  const next = trimStringsDeep(JSON.parse(JSON.stringify(content)));

  if (Array.isArray(next.bookings?.availableForItems)) {
    next.bookings.availableForItems = next.bookings.availableForItems.filter(Boolean);
  }

  if (Array.isArray(next.lessons?.options)) {
    next.lessons.options = next.lessons.options.map((option) => ({
      ...option,
      details: Array.isArray(option.details) ? option.details.filter(Boolean) : []
    }));
  }

  return enforceFixedPageTitles(next);
};

export const preparePageContentForSave = (content) => sanitizePageContent(content);

export const getNestedValue = (obj, path) =>
  path.split('.').reduce((current, key) => (current == null ? undefined : current[key]), obj);

const isArrayIndex = (key) => /^\d+$/.test(key);

export const setNestedValue = (obj, path, value) => {
  const keys = path.split('.');
  const result = JSON.parse(JSON.stringify(obj));
  let current = result;

  keys.forEach((key, index) => {
    const isLast = index === keys.length - 1;

    if (isLast) {
      if (Array.isArray(current) && isArrayIndex(key)) {
        current[Number(key)] = value;
      } else {
        current[key] = value;
      }
      return;
    }

    const nextKey = keys[index + 1];

    if (Array.isArray(current) && isArrayIndex(key)) {
      const arrayIndex = Number(key);
      if (current[arrayIndex] == null) {
        current[arrayIndex] = isArrayIndex(nextKey) ? [] : {};
      }
      current = current[arrayIndex];
      return;
    }

    if (current[key] == null) {
      current[key] = isArrayIndex(nextKey) ? [] : {};
    }
    current = current[key];
  });

  return result;
};

export const applyFieldUpdates = (pageContent, fields, draft) => {
  let next = pageContent;

  fields.forEach((field) => {
    let value = draft[field.name];

    if (field.type === 'lines') {
      value = Array.isArray(value)
        ? value.map((line) => String(line).trim()).filter(Boolean)
        : String(value || '')
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean);
    }

    next = setNestedValue(next, field.path, value);
  });

  return next;
};
